// Texts of the examples area (GIOTHUB-185). English defines the keys.
import type { Dict } from "./dict";

const en = {
  "ex.title": "Examples",
  "ex.pass": "{passing} of {total} pass",
  "ex.run": "Run",
  "ex.run.title": "Run every example (pending proposals applied)",
  "ex.new": "New example",
  "ex.empty": "No examples yet. Add payloads with the values they must decode to: they are the specification the decoder must meet.",
  "ex.ai": "AI",
  "ex.byai": "added by the assistant",
  "ex.howto": "How to write examples",
  "ex.fromdecoder.note": "The expected values were taken from the decoding when the example was saved: check that they are right.",
  "ex.retake.title": "Decode the payload again with the package as it is now (after review changes) and store the result as expected",
  "ex.retake": "Take the expected values again",
  "ex.decoding": "Decoding…",
  "ex.message": "Generated from this message (protobuf JSON)",
  "ex.name": "Name",
  "ex.receivedat": "Received at (optional)",
  "ex.serial": "Device serial (optional)",
  "ex.serial.ph": "IMEI or DevEUI",
  "ex.topic": "Topic (optional)",
  "ex.topic.ph": "the MQTT topic the payload arrived on, if the decoder needs it",
  "ex.result": "Result document of the logic module (JSON)",
  "ex.module.aria": "Use a module example",
  "ex.module.pick": "Use one of the module's examples…",
  "ex.result.aria": "Result document",
  "ex.result.hint": "The decoder reads this document, not the radio payload: the module has already parsed the frame, recovered the samples and their times. Write the values it must produce below.",
  "ex.payload": "Payload",
  "ex.payload.format": "Payload format",
  "ex.payload.text.title": "Type the payload as text (stored as its UTF-8 bytes)",
  "ex.payload.hex.title": "Type the payload as hex bytes",
  "ex.payload.hex": "Hex",
  "ex.payload.text": "Text",
  "ex.payload.nottext": "This payload is not text (it has bytes that are not printable UTF-8): keep it in hex.",
  "ex.loadbin": "Load a .bin capture",
  "ex.payload.text.aria": "Payload as text",
  "ex.payload.hex.aria": "Payload as hex",
  "ex.astext": "As text:",
  "ex.fromdecoder": "Take the expected values from the current decoding of the package",
  "ex.fromdecoder.hint": "For a protobuf device the decoding follows from the .proto and your review: the builder decodes the payload now and stores the result as expected. Check it below after saving - from then on the sample protects it.",
  "ex.expected": "Expected values (the decoder's result: state, timeseries, events, config - or errors)",
  "ex.badjson.expected": "Expected values are not valid JSON: {error}",
  "ex.badjson.result": "The result document is not valid JSON: {error}",
  "ex.saving": "Saving…",
  "ex.save": "Save example",
  "ex.ok": "Decodes as expected",
  "ex.notok": "Does not decode as expected",
  "ex.duration": "· {ms} ms in the store's sandbox, with the pending proposals",
  "ex.geo": "For the geolocation service:",
  "ex.geo.wifi": "{count} Wi-Fi",
  "ex.geo.cells": "{count} cells",
  "ex.geo.ble": "{count} BLE",
  "ex.geo.show": "Show the extracted data",
  "ex.got": "What the decoder returned",
} as const;

const de = {
  "ex.title": "Beispiele",
  "ex.pass": "{passing} von {total} bestanden",
  "ex.run": "Ausführen",
  "ex.run.title": "Alle Beispiele ausführen (offene Vorschläge angewendet)",
  "ex.new": "Neues Beispiel",
  "ex.empty": "Noch keine Beispiele. Fügen Sie Payloads mit den Werten hinzu, zu denen sie dekodiert werden müssen: Sie sind die Spezifikation, die der Decoder erfüllen muss.",
  "ex.ai": "KI",
  "ex.byai": "vom Assistenten hinzugefügt",
  "ex.howto": "Wie man Beispiele schreibt",
  "ex.fromdecoder.note": "Die erwarteten Werte wurden beim Speichern des Beispiels aus der Dekodierung übernommen: Prüfen Sie, ob sie stimmen.",
  "ex.retake.title": "Die Payload mit dem aktuellen Paket (nach den Änderungen der Prüfung) erneut dekodieren und das Ergebnis als erwartet speichern",
  "ex.retake": "Erwartete Werte neu übernehmen",
  "ex.decoding": "Wird dekodiert…",
  "ex.message": "Aus dieser Nachricht erzeugt (protobuf JSON)",
  "ex.name": "Name",
  "ex.receivedat": "Empfangen am (optional)",
  "ex.serial": "Seriennummer des Geräts (optional)",
  "ex.serial.ph": "IMEI oder DevEUI",
  "ex.topic": "Topic (optional)",
  "ex.topic.ph": "das MQTT-Topic, auf dem die Payload ankam, falls der Decoder es braucht",
  "ex.result": "Ergebnisdokument des Logikmoduls (JSON)",
  "ex.module.aria": "Ein Beispiel des Moduls verwenden",
  "ex.module.pick": "Eines der Beispiele des Moduls verwenden…",
  "ex.result.aria": "Ergebnisdokument",
  "ex.result.hint": "Der Decoder liest dieses Dokument, nicht die Funk-Payload: Das Modul hat den Frame bereits zerlegt und die Messwerte mit ihren Zeiten gewonnen. Tragen Sie unten die Werte ein, die er liefern muss.",
  "ex.payload": "Payload",
  "ex.payload.format": "Format der Payload",
  "ex.payload.text.title": "Payload als Text eingeben (gespeichert als ihre UTF-8-Bytes)",
  "ex.payload.hex.title": "Payload als Hex-Bytes eingeben",
  "ex.payload.hex": "Hex",
  "ex.payload.text": "Text",
  "ex.payload.nottext": "Diese Payload ist kein Text (sie enthält Bytes, die kein druckbares UTF-8 sind): Belassen Sie sie in Hex.",
  "ex.loadbin": "Eine .bin-Aufzeichnung laden",
  "ex.payload.text.aria": "Payload als Text",
  "ex.payload.hex.aria": "Payload als Hex",
  "ex.astext": "Als Text:",
  "ex.fromdecoder": "Erwartete Werte aus der aktuellen Dekodierung des Pakets übernehmen",
  "ex.fromdecoder.hint": "Bei einem protobuf-Gerät ergibt sich die Dekodierung aus der .proto und Ihrer Prüfung: Der Builder dekodiert die Payload jetzt und speichert das Ergebnis als erwartet. Prüfen Sie es nach dem Speichern unten - ab dann schützt das Beispiel es.",
  "ex.expected": "Erwartete Werte (das Ergebnis des Decoders: state, timeseries, events, config - oder errors)",
  "ex.badjson.expected": "Die erwarteten Werte sind kein gültiges JSON: {error}",
  "ex.badjson.result": "Das Ergebnisdokument ist kein gültiges JSON: {error}",
  "ex.saving": "Wird gespeichert…",
  "ex.save": "Beispiel speichern",
  "ex.ok": "Dekodiert wie erwartet",
  "ex.notok": "Dekodiert nicht wie erwartet",
  "ex.duration": "· {ms} ms in der Sandbox des Stores, mit den offenen Vorschlägen",
  "ex.geo": "Für den Geolokalisierungsdienst:",
  "ex.geo.wifi": "{count} WLAN",
  "ex.geo.cells": "{count} Zellen",
  "ex.geo.ble": "{count} BLE",
  "ex.geo.show": "Extrahierte Daten anzeigen",
  "ex.got": "Was der Decoder zurückgab",
} satisfies Dict<typeof en>;

const it = {
  "ex.title": "Esempi",
  "ex.pass": "{passing} di {total} superati",
  "ex.run": "Esegui",
  "ex.run.title": "Esegui tutti gli esempi (con le proposte in sospeso applicate)",
  "ex.new": "Nuovo esempio",
  "ex.empty": "Ancora nessun esempio. Aggiunga dei payload con i valori in cui devono essere decodificati: sono la specifica che il decoder deve rispettare.",
  "ex.ai": "IA",
  "ex.byai": "aggiunto dall'assistente",
  "ex.howto": "Come scrivere gli esempi",
  "ex.fromdecoder.note": "I valori attesi sono stati presi dalla decodifica al salvataggio dell'esempio: verifichi che siano corretti.",
  "ex.retake.title": "Decodifica di nuovo il payload con il pacchetto attuale (dopo le modifiche della revisione) e salva il risultato come atteso",
  "ex.retake": "Riprendi i valori attesi",
  "ex.decoding": "Decodifica in corso…",
  "ex.message": "Generato da questo messaggio (protobuf JSON)",
  "ex.name": "Nome",
  "ex.receivedat": "Ricevuto il (facoltativo)",
  "ex.serial": "Numero di serie del dispositivo (facoltativo)",
  "ex.serial.ph": "IMEI o DevEUI",
  "ex.topic": "Topic (facoltativo)",
  "ex.topic.ph": "il topic MQTT su cui è arrivato il payload, se il decoder ne ha bisogno",
  "ex.result": "Documento di risultato del modulo logico (JSON)",
  "ex.module.aria": "Usa un esempio del modulo",
  "ex.module.pick": "Usa uno degli esempi del modulo…",
  "ex.result.aria": "Documento di risultato",
  "ex.result.hint": "Il decoder legge questo documento, non il payload radio: il modulo ha già interpretato il frame e ricavato i campioni con i loro orari. Scriva qui sotto i valori che deve produrre.",
  "ex.payload": "Payload",
  "ex.payload.format": "Formato del payload",
  "ex.payload.text.title": "Scriva il payload come testo (salvato come i suoi byte UTF-8)",
  "ex.payload.hex.title": "Scriva il payload come byte esadecimali",
  "ex.payload.hex": "Hex",
  "ex.payload.text": "Testo",
  "ex.payload.nottext": "Questo payload non è testo (contiene byte che non sono UTF-8 stampabile): lo lasci in esadecimale.",
  "ex.loadbin": "Carica una cattura .bin",
  "ex.payload.text.aria": "Payload come testo",
  "ex.payload.hex.aria": "Payload in esadecimale",
  "ex.astext": "Come testo:",
  "ex.fromdecoder": "Prendi i valori attesi dalla decodifica attuale del pacchetto",
  "ex.fromdecoder.hint": "Per un dispositivo protobuf la decodifica deriva dal .proto e dalla Sua revisione: il builder decodifica ora il payload e salva il risultato come atteso. Lo verifichi qui sotto dopo il salvataggio - da quel momento l'esempio lo protegge.",
  "ex.expected": "Valori attesi (il risultato del decoder: state, timeseries, events, config - oppure errors)",
  "ex.badjson.expected": "I valori attesi non sono JSON valido: {error}",
  "ex.badjson.result": "Il documento di risultato non è JSON valido: {error}",
  "ex.saving": "Salvataggio…",
  "ex.save": "Salva l'esempio",
  "ex.ok": "Decodifica come atteso",
  "ex.notok": "Non decodifica come atteso",
  "ex.duration": "· {ms} ms nella sandbox dello store, con le proposte in sospeso",
  "ex.geo": "Per il servizio di geolocalizzazione:",
  "ex.geo.wifi": "{count} Wi-Fi",
  "ex.geo.cells": "{count} celle",
  "ex.geo.ble": "{count} BLE",
  "ex.geo.show": "Mostra i dati estratti",
  "ex.got": "Cosa ha restituito il decoder",
} satisfies Dict<typeof en>;

const fr = {
  "ex.title": "Exemples",
  "ex.pass": "{passing} sur {total} réussis",
  "ex.run": "Exécuter",
  "ex.run.title": "Exécuter tous les exemples (propositions en attente appliquées)",
  "ex.new": "Nouvel exemple",
  "ex.empty": "Aucun exemple pour l'instant. Ajoutez des payloads avec les valeurs qu'ils doivent donner au décodage : ils sont la spécification que le décodeur doit respecter.",
  "ex.ai": "IA",
  "ex.byai": "ajouté par l'assistant",
  "ex.howto": "Comment écrire des exemples",
  "ex.fromdecoder.note": "Les valeurs attendues ont été reprises du décodage lors de l'enregistrement de l'exemple : vérifiez qu'elles sont justes.",
  "ex.retake.title": "Décoder à nouveau le payload avec le paquet tel qu'il est maintenant (après les modifications de la revue) et enregistrer le résultat comme attendu",
  "ex.retake": "Reprendre les valeurs attendues",
  "ex.decoding": "Décodage…",
  "ex.message": "Généré à partir de ce message (protobuf JSON)",
  "ex.name": "Nom",
  "ex.receivedat": "Reçu le (facultatif)",
  "ex.serial": "Numéro de série de l'appareil (facultatif)",
  "ex.serial.ph": "IMEI ou DevEUI",
  "ex.topic": "Topic (facultatif)",
  "ex.topic.ph": "le topic MQTT sur lequel le payload est arrivé, si le décodeur en a besoin",
  "ex.result": "Document de résultat du module logique (JSON)",
  "ex.module.aria": "Utiliser un exemple du module",
  "ex.module.pick": "Utiliser un des exemples du module…",
  "ex.result.aria": "Document de résultat",
  "ex.result.hint": "Le décodeur lit ce document, pas le payload radio : le module a déjà analysé la trame et récupéré les mesures avec leurs heures. Indiquez ci-dessous les valeurs qu'il doit produire.",
  "ex.payload": "Payload",
  "ex.payload.format": "Format du payload",
  "ex.payload.text.title": "Saisir le payload comme texte (enregistré sous forme d'octets UTF-8)",
  "ex.payload.hex.title": "Saisir le payload en octets hexadécimaux",
  "ex.payload.hex": "Hex",
  "ex.payload.text": "Texte",
  "ex.payload.nottext": "Ce payload n'est pas du texte (il contient des octets qui ne sont pas de l'UTF-8 imprimable) : gardez-le en hexadécimal.",
  "ex.loadbin": "Charger une capture .bin",
  "ex.payload.text.aria": "Payload en texte",
  "ex.payload.hex.aria": "Payload en hexadécimal",
  "ex.astext": "En texte :",
  "ex.fromdecoder": "Reprendre les valeurs attendues du décodage actuel du paquet",
  "ex.fromdecoder.hint": "Pour un appareil protobuf, le décodage découle du .proto et de votre revue : le builder décode le payload maintenant et enregistre le résultat comme attendu. Vérifiez-le ci-dessous après l'enregistrement - dès lors, l'exemple le protège.",
  "ex.expected": "Valeurs attendues (le résultat du décodeur : state, timeseries, events, config - ou errors)",
  "ex.badjson.expected": "Les valeurs attendues ne sont pas du JSON valide : {error}",
  "ex.badjson.result": "Le document de résultat n'est pas du JSON valide : {error}",
  "ex.saving": "Enregistrement…",
  "ex.save": "Enregistrer l'exemple",
  "ex.ok": "Décode comme attendu",
  "ex.notok": "Ne décode pas comme attendu",
  "ex.duration": "· {ms} ms dans le bac à sable du store, avec les propositions en attente",
  "ex.geo": "Pour le service de géolocalisation :",
  "ex.geo.wifi": "{count} Wi-Fi",
  "ex.geo.cells": "{count} cellules",
  "ex.geo.ble": "{count} BLE",
  "ex.geo.show": "Afficher les données extraites",
  "ex.got": "Ce que le décodeur a renvoyé",
} satisfies Dict<typeof en>;

export default { en, de, it, fr };
